import { Box } from "@mui/material";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_QUERY,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { SiteImage } from "../siteKit/media/SiteImage";
import {
  siteFileUrl,
  siteImageUrl,
  type SiteImageName,
} from "../siteKit/media/siteMedia";
import { useReducedMotion } from "../siteKit/motion/reducedMotion";
import { revealRef } from "../siteKit/motion/reveal";
import { usePageVisible } from "../siteKit/motion/usePageVisible";
import { SectionHead } from "../siteKit/SectionHead";
import { SiteSection } from "../siteKit/SiteSection";
import type { ResultsSceneController } from "../scenes/resultsScene";

/** 결과물 하나를 보여 주는 시간(ms) — 진행 막대가 이 시간 동안 찬다 */
const RESULT_DURATION_MS = 3600;
/** 이전 그림은 새 그림이 다 나타난 뒤 내린다(바탕이 비치며 깜빡이지 않게) */
const UNDER_RELEASE_MS = 800;
const VISIBLE_THRESHOLD = 0.15;
/** 3D 장면 데이터는 화면 이만큼 앞에서 받기 시작한다 */
const SCENE_ROOT_MARGIN = "900px";
const COLORS_IMAGE_WIDTH = 960;
const TEXTURE_IMAGE_WIDTH = 1900;

/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 시안 `--cis`·`--cism` */
const RESULTS_OFFSCREEN_SIZE = [1100, 1500] as const;

const RESULT_IMAGES: readonly SiteImageName[] = [
  "res_ortho",
  "res_dsm",
  "res_pc",
  "res_mesh",
  "res_contour",
];

const RESULTS_STYLES = {
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0,300px) minmax(0,1fr)",
    gap: "clamp(32px,5vw,80px)",
    alignItems: "center",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
    // 가로 터치 화면: 탭 줄 아래 그림 대신 넓은 화면처럼 탭 열 | 그림 두 칸
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "minmax(200px,260px) minmax(0,1fr)",
      gap: "24px",
    },
  },
  tabs: {
    display: "flex",
    flexDirection: "column",
    [SITE_MEDIA.tablet]: {
      flexDirection: "row",
      overflowX: "auto",
      scrollbarWidth: "none",
      gap: "20px",
    },
    [SITE_MEDIA.landscapeTouch]: {
      flexDirection: "column",
      overflowX: "visible",
      gap: "4px",
    },
  },
  tab: {
    position: "relative",
    textAlign: "left",
    padding: "16px 0 16px 20px",
    color: SITE_COLOR.ink3,
    transition: "color .25s",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "2px",
      background: SITE_COLOR.line,
    },
    "& [data-bar]": {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "2px",
      background: SITE_COLOR.ink,
      transform: "scaleY(0)",
      transformOrigin: "top",
    },
    "& b": {
      display: "block",
      fontSize: "19px",
      fontWeight: 700,
      lineHeight: 1.4,
    },
    "& small": { display: "block", fontSize: "14px", marginTop: "2px" },
    '&[aria-selected="true"]': { color: SITE_COLOR.ink },
    '&[aria-selected="true"] small': { color: SITE_COLOR.ink2 },
    [SITE_MEDIA.tablet]: {
      // 손가락으로 누르는 높이 44px(글자 줄 27px + 위 5px + 아래 12px)
      padding: "5px 0 12px",
      flex: "none",
      "&::before": {
        left: 0,
        right: 0,
        top: "auto",
        bottom: 0,
        width: "auto",
        height: "2px",
      },
      "& [data-bar]": {
        top: "auto",
        bottom: 0,
        right: 0,
        width: "auto",
        height: "2px",
        transform: "scaleX(0)",
        transformOrigin: "left",
      },
      "& small": { display: "none" },
    },
  },
  view: {
    position: "relative",
    aspectRatio: "16/10",
    // 가로 터치 화면: 오른쪽 칸의 그림(16:10)이 머리말 아래 화면 높이를 넘지 않는 폭까지(비율을 지키며 칸 가운데)
    [SITE_MEDIA.landscapeTouch]: {
      width: "100%",
      maxWidth: `calc((100svh - ${SITE_HEADER_HEIGHT_PX}px - 24px) * 1.6)`,
      justifySelf: "center",
    },
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    background: SITE_COLOR.panel,
    "& > img": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      opacity: 0,
      transform: "scale(1.03)",
      transition: `opacity .7s ease,transform 1.2s ${SITE_EASE.out}`,
    },
    '& > img[data-shown="on"]': { opacity: 1, transform: "none", zIndex: 2 },
    '& > img[data-shown="under"]': {
      opacity: 1,
      transform: "none",
      zIndex: 1,
      transition: "none",
    },
    "& > canvas": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      display: "block",
      opacity: 0,
      transition: "opacity .6s ease",
      zIndex: 3,
    },
  },
} as const;

interface ResultTabText {
  name: string;
  note: string;
  alt: string;
}

/**
 * 드론 측량 결과물(marketing-service-pages): 결과물 종류 탭 + 한 현장을 이어서 보여 주는 3D 장면.
 * 탭은 보이는 동안 3.6초마다 넘어가고(진행 막대), 누르거나 화살표 키로 고를 수 있다. WebGL 을 못 쓰면 미리 찍은 그림을 겹쳐 바꾼다.
 */
export function SurveyResults() {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const tabs = t("site.survey.results.tabs", {
    returnObjects: true,
  }) as readonly ResultTabText[];
  const boxRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const sceneRef = useRef<ResultsSceneController | null>(null);
  const visibleRef = useRef(false);
  const [index, setIndex] = useState(0);
  const [under, setUnder] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);
  const pageVisible = usePageVisible();
  const reducedMotion = useReducedMotion();
  const active = visible && pageVisible;

  const select = (next: number) => {
    setUnder((previousUnder) => (next === index ? previousUnder : index));
    setIndex(next);
  };

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = visibleRef.current;
        visibleRef.current = entry.isIntersecting;
        setVisible(entry.isIntersecting);
        // 화면에 새로 들어오면 첫 결과물부터
        if (entry.isIntersecting && !wasVisible) {
          setUnder(null);
          setIndex(0);
        }
      },
      { threshold: VISIBLE_THRESHOLD }
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  // 화면 가까이 오면 3D 장면을 만든다(코드·데이터 모두 이때 받는다)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return undefined;
    let disposed = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void (async () => {
          const [
            { canUseWebGL, loadImage, loadTextureFactory },
            { createResultsScene },
          ] = await Promise.all([
            import("../scenes/sceneAssets"),
            import("../scenes/resultsScene"),
          ]);
          if (disposed || !canUseWebGL()) return;
          const [ortho, height, colors] = await Promise.all([
            loadTextureFactory(
              siteFileUrl("data/ortho_now.ktx2"),
              siteImageUrl("ortho_now", TEXTURE_IMAGE_WIDTH)
            ),
            loadImage(siteFileUrl("data/res_h.png")),
            loadImage(siteImageUrl("ortho_now", COLORS_IMAGE_WIDTH)),
          ]);
          if (disposed) return;
          const scene = await createResultsScene(
            view,
            { ortho, height, colors },
            () => visibleRef.current && !document.hidden,
            reducedMotion
          );
          if (disposed) {
            scene.dispose();
            return;
          }
          sceneRef.current = scene;
          setIndex((current) => {
            scene.go(current, true);
            return current;
          });
        })().catch(() => {
          sceneRef.current = null;
        });
      },
      { rootMargin: SCENE_ROOT_MARGIN }
    );
    observer.observe(view);
    return () => {
      disposed = true;
      observer.disconnect();
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, [reducedMotion]);

  // 결과물이 바뀌면 장면을 넘기고, 진행 막대를 채우고, 보이는 동안 다음으로
  useEffect(() => {
    sceneRef.current?.go(index);
    const bar =
      tabRefs.current[index]?.querySelector<HTMLElement>("[data-bar]");
    const horizontal = window.matchMedia(SITE_QUERY.tablet).matches;
    const axis = horizontal ? "scaleX" : "scaleY";
    let animation: Animation | null = null;
    if (bar) {
      bar.getAnimations().forEach((running) => running.cancel());
      if (reducedMotion) bar.style.transform = `${axis}(1)`;
      else {
        bar.style.transform = "";
        animation = bar.animate(
          [{ transform: `${axis}(0)` }, { transform: `${axis}(1)` }],
          {
            duration: RESULT_DURATION_MS,
            easing: "linear",
            fill: "forwards",
          }
        );
        if (!active) animation.pause();
      }
    }
    if (active) sceneRef.current?.wake();
    const timer =
      active && !reducedMotion
        ? window.setTimeout(
            () => select((index + 1) % tabs.length),
            RESULT_DURATION_MS
          )
        : 0;
    return () => {
      window.clearTimeout(timer);
      animation?.cancel();
      tabRefs.current.forEach((tab) => {
        const tabBar = tab?.querySelector<HTMLElement>("[data-bar]");
        if (tabBar && tab !== tabRefs.current[index])
          tabBar.style.transform = "";
      });
    };
  }, [index, active, reducedMotion, tabs.length]);

  // 대체 그림: 새 그림이 다 나타난 뒤 이전 그림을 내린다
  useEffect(() => {
    if (under === null) return undefined;
    const timer = window.setTimeout(() => setUnder(null), UNDER_RELEASE_MS);
    return () => window.clearTimeout(timer);
  }, [under]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    tabIndex: number
  ) => {
    const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
    const backward = event.key === "ArrowUp" || event.key === "ArrowLeft";
    if (!forward && !backward) return;
    event.preventDefault();
    const next = (tabIndex + (forward ? 1 : -1) + tabs.length) % tabs.length;
    tabRefs.current[next]?.focus();
    select(next);
  };

  const shownOf = (imageIndex: number) => {
    if (imageIndex === index) return "on";
    if (imageIndex === under) return "under";
    return undefined;
  };

  return (
    <SiteSection labelledBy={titleId} skipOffscreen={RESULTS_OFFSCREEN_SIZE}>
      <Box sx={SITE_WRAP}>
        <SectionHead
          titleId={titleId}
          eyebrow={t("site.survey.results.eyebrow")}
          title={t("site.survey.results.title")}
          lead={t("site.survey.results.lead")}
        />
        <Box ref={boxRef} sx={RESULTS_STYLES.layout}>
          <Box ref={revealRef} data-reveal="up">
            <Box
              role="tablist"
              aria-label={t("site.survey.results.tabsLabel")}
              sx={RESULTS_STYLES.tabs}
            >
              {tabs.map((tab, tabIndex) => (
                <Box
                  component="button"
                  type="button"
                  role="tab"
                  key={tab.name}
                  ref={(element: HTMLButtonElement | null) => {
                    tabRefs.current[tabIndex] = element;
                  }}
                  aria-selected={tabIndex === index}
                  tabIndex={tabIndex === index ? 0 : -1}
                  onClick={() => select(tabIndex)}
                  onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) =>
                    handleKeyDown(event, tabIndex)
                  }
                  sx={RESULTS_STYLES.tab}
                >
                  <i data-bar="" />
                  <b>{tab.name}</b>
                  <small>{tab.note}</small>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            component="figure"
            ref={(element: HTMLElement | null) => {
              viewRef.current = element;
              return revealRef(element);
            }}
            data-reveal="img"
            sx={RESULTS_STYLES.view}
          >
            {RESULT_IMAGES.map((name, imageIndex) => (
              <SiteImage
                key={name}
                name={name}
                sizes="(max-width: 900px) 100vw, 860px"
                alt={tabs[imageIndex]?.alt ?? ""}
                placeholder="none"
                data-shown={shownOf(imageIndex)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </SiteSection>
  );
}
